import type { Transaction } from "@/model/DataModel"
import { AreaUnit, convertArea, convertPricePerArea } from "@/utils/unitSystem"

export const TransactionAreaField = {
	TotalLandArea: "superficy",
	BuildableArea: "buildableAreaSqft",
	UnitArea: "unitAreaSqft",
	DevelopableLandArea: "developableLandAreaSqft",
	NetLandArea: "netLandAreaSqft",
} as const

export type TransactionAreaField = (typeof TransactionAreaField)[keyof typeof TransactionAreaField]

export const TransactionPricePerAreaField = {
	Land: "pricePerLandSqft",
	Developable: "pricePerDevelopableSqft",
	Buildable: "pricePerBuildableSqft",
} as const

export type TransactionPricePerAreaField =
	(typeof TransactionPricePerAreaField)[keyof typeof TransactionPricePerAreaField]

const editableAreaFields = new Set<string>([TransactionAreaField.BuildableArea, TransactionAreaField.NetLandArea])

function convertOptionalArea(
	value: number | null | undefined,
	sourceUnit: AreaUnit,
	targetUnit: AreaUnit
): number | null | undefined {
	if (value == null || !Number.isFinite(value)) {
		return value
	}

	return convertArea(value, sourceUnit, targetUnit)
}

export function transactionAreaValue(
	transaction: Transaction,
	field: TransactionAreaField,
	targetUnit: AreaUnit
): number | null | undefined {
	if (field === TransactionAreaField.TotalLandArea) {
		const preferredValue =
			targetUnit === AreaUnit.SquareMeters ? transaction.superficy?.squareMeters : transaction.superficy?.squareFeet

		if (preferredValue != null && Number.isFinite(preferredValue)) {
			return preferredValue
		}

		const fallbackValue =
			targetUnit === AreaUnit.SquareMeters ? transaction.superficy?.squareFeet : transaction.superficy?.squareMeters
		const fallbackUnit = targetUnit === AreaUnit.SquareMeters ? AreaUnit.SquareFeet : AreaUnit.SquareMeters

		return convertOptionalArea(fallbackValue, fallbackUnit, targetUnit)
	}

	return convertOptionalArea(transaction[field], AreaUnit.SquareFeet, targetUnit)
}

export function transactionPricePerAreaValue(
	transaction: Transaction,
	field: TransactionPricePerAreaField,
	targetUnit: AreaUnit
): number | null | undefined {
	const value = transaction[field]

	if (value == null || !Number.isFinite(value)) {
		return value
	}

	return convertPricePerArea(value, AreaUnit.SquareFeet, targetUnit)
}

export function transactionAreaInputValue(field: string, value: unknown, sourceUnit: AreaUnit): unknown {
	if (!editableAreaFields.has(field) || typeof value !== "number" || !Number.isFinite(value)) {
		return value
	}

	return convertArea(value, sourceUnit, AreaUnit.SquareFeet)
}
